import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Upload, FileSpreadsheet, CheckCircle2, AlertCircle, Database, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminImport = () => {
  const [mode, setMode] = useState('csv'); // 'csv' | 'sheet'
  const [file, setFile] = useState(null);
  const [sheetUrl, setSheetUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errOpen, setErrOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [job, setJob] = useState(null);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearTimeout(pollRef.current), []);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/creator-profiles/stats`, { withCredentials: true });
      setStats(data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => { fetchStats(); }, []);

  const handleFile = (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) {
      toast.error('Please upload a .csv file');
      return;
    }
    setFile(f);
  };

  const onDrop = (e) => {
    e.preventDefault();
    handleFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    if (mode === 'csv' && !file) {
      toast.error('Choose a CSV file first');
      return;
    }
    if (mode === 'sheet' && !sheetUrl.trim()) {
      toast.error('Paste a Google Sheets URL first');
      return;
    }
    setLoading(true);
    setResult(null);
    setJob(null);
    try {
      const form = new FormData();
      if (mode === 'csv') form.append('file', file);
      else form.append('sheet_url', sheetUrl.trim());
      const { data } = await axios.post(`${API_URL}/api/admin/import-creators`, form, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data.job_id) {
        setResult(data);
        toast.success(`Imported ${data.inserted_count} creators, updated ${data.updated_count}, skipped ${data.skipped_count}`);
        fetchStats();
        setLoading(false);
        return;
      }
      setJob(data);
      poll(data.job_id);
    } catch (e) {
      console.error(e);
      toast.error(errText(e, 'Import failed'));
      setLoading(false);
    }
  };

  const poll = (id, misses = 0) => {
    pollRef.current = setTimeout(async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/admin/import-jobs/${id}`, { withCredentials: true });
        setJob(data);
        if (data.status === 'running') return poll(id);
        setLoading(false);
        setResult(data);
        fetchStats();
        if (data.status === 'done') toast.success(`Imported ${data.inserted_count.toLocaleString()} creators, updated ${data.updated_count.toLocaleString()}, skipped ${data.skipped_count.toLocaleString()}`, { description: data.merged_count ? `${data.merged_count.toLocaleString()} duplicate ${data.merged_count === 1 ? 'row was' : 'rows were'} merged into an existing creator` : undefined });
        else toast.error(data.error || 'Import failed', { duration: 10000 });
      } catch (e) {
        if (misses < 5) return poll(id, misses + 1);
        setLoading(false);
        toast.error(errText(e, 'Lost contact with the import — refresh to check the creator count'));
      }
      return undefined;
    }, 1500);
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="admin-import-page">
        <div className="max-w-5xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
              <Database className="text-[#FF451A]" size={32} /> Creator Directory Import
            </h1>
            <p className="text-[#6B6B6B] dark:text-zinc-400">Upload a CSV or paste a Google Sheets URL to bulk-load creator profiles. Records upsert on Instagram handle.</p>
          </div>

          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              <Stat label="Total Creators" value={stats.total?.toLocaleString() || 0} data-testid="stat-total" />
              <Stat label="From Imports" value={stats.imported?.toLocaleString() || 0} accent="#FFB800" />
              <Stat label="Grade A" value={stats.by_quality?.A || 0} accent="#00C972" />
              <Stat label="Grade B" value={stats.by_quality?.B || 0} accent="#0055FF" />
              <Stat label="Grade C / D" value={(stats.by_quality?.C || 0) + (stats.by_quality?.D || 0)} accent="#FFB800" />
            </div>
          )}

          <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6">
            <div className="flex items-center gap-2 mb-5">
              <button
                onClick={() => setMode('csv')}
                className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${mode === 'csv' ? 'bg-[#FF451A]/10 text-[#FF451A]' : 'text-[#6B6B6B] dark:text-zinc-500 hover:text-white'}`}
                data-testid="mode-csv"
              >
                <Upload size={14} /> CSV File
              </button>
              <button
                onClick={() => setMode('sheet')}
                className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${mode === 'sheet' ? 'bg-[#00C972]/10 text-[#00C972]' : 'text-[#6B6B6B] dark:text-zinc-500 hover:text-white'}`}
                data-testid="mode-sheet"
              >
                <FileSpreadsheet size={14} /> Google Sheets URL
              </button>
            </div>

            {mode === 'csv' ? (
              <div
                onDragOver={(e) => e.preventDefault()}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-black/[0.10] dark:border-white/10 hover:border-[#FF451A]/30 rounded-xl p-8 text-center cursor-pointer transition-colors"
                data-testid="csv-dropzone"
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => handleFile(e.target.files?.[0])}
                  data-testid="csv-file-input"
                />
                <Upload className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={32} />
                {file ? (
                  <div>
                    <div className="text-[#0F0F0F] dark:text-white font-medium">{file.name}</div>
                    <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-1">{(file.size / 1024).toFixed(1)} KB · click to swap</div>
                  </div>
                ) : (
                  <div>
                    <div className="text-[#0F0F0F] dark:text-white font-medium">Drop your CSV here, or click to browse</div>
                    <div className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-1">Headers (any case): name, ig_handle, ig_link, followers, contact, yt_link, yt_subscribers, category, state, city, gender, barter, reel_cost, story_cost, age, language, avg_views, cpv, quality, manager_name. Rows upsert on ig_handle.</div>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <label className="font-mono text-[10px] uppercase tracking-[0.2em] block mb-2 text-[#0F0F0F] dark:text-white">Google Sheets share URL</label>
                <Input
                  type="url"
                  value={sheetUrl}
                  onChange={(e) => setSheetUrl(e.target.value)}
                  placeholder="https://docs.google.com/spreadsheets/d/.../edit#gid=0"
                  className="bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                  data-testid="sheet-url-input"
                />
                <p className="text-xs text-[#6B6B6B] dark:text-zinc-500 mt-2">Sheet must be shared as <span className="text-[#FFB800]">"Anyone with the link can view"</span>.</p>
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <Button
                onClick={submit}
                disabled={loading}
                className="bg-[#FF451A] hover:bg-[#E63E17] text-[#0F0F0F] dark:text-white rounded-full px-6 py-2.5 font-medium disabled:opacity-50"
                data-testid="import-submit-button"
              >
                {loading ? (
                  <><Loader2 className="animate-spin mr-2" size={16} /> Importing…</>
                ) : (
                  <>Run Import</>
                )}
              </Button>
            </div>
            {job && job.status === 'running' && <ImportProgress job={job} />}
          </div>

          {result && result.status === 'failed' && (
            <div className="mt-6 border border-[#FF3B30]/30 bg-[#FF3B30]/5 rounded-2xl p-5 flex items-start gap-3" data-testid="import-error-panel">
              <AlertCircle className="text-[#FF3B30] shrink-0" size={20} />
              <div className="text-sm text-[#0F0F0F] dark:text-white"><div className="font-semibold mb-1">Import failed</div>{result.error}</div>
            </div>
          )}

          {result && (
            <div className="mt-6 bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-6" data-testid="import-result-panel">
              <div className="flex items-center gap-2 mb-5">
                {result.status === 'failed' ? <AlertCircle className="text-[#FF3B30]" size={22} /> : <CheckCircle2 className="text-[#00C972]" size={22} />}
                <h2 className="text-xl font-semibold text-[#0F0F0F] dark:text-white">{result.status === 'failed' ? 'Partial results before the import stopped' : 'Import Summary'}</h2>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
                <Cell label="Total Rows" value={result.total_rows} />
                <Cell label="New" value={result.inserted_count} accent="#00C972" />
                <Cell label="Updated" value={result.updated_count} accent="#0055FF" />
                <Cell label="Duplicates merged" value={result.merged_count || 0} accent="#FFB800" />
                <Cell label="Skipped" value={result.skipped_count} accent={result.skipped_count > 0 ? '#FF3B30' : '#666'} />
              </div>

              {result.skip_reasons && Object.keys(result.skip_reasons).length > 0 && (
                <div className="mb-5 flex flex-wrap gap-2" data-testid="skip-reasons">
                  {Object.entries(result.skip_reasons).map(([k, n]) => (
                    <span key={k} className="px-2.5 py-1 text-xs rounded-full bg-[#FF3B30]/10 text-[#B42318] dark:text-[#FF8A80]">{k}: <b>{n.toLocaleString()}</b></span>
                  ))}
                </div>
              )}

              {(result.discoverable_count != null) && (
                <div
                  className={`mb-5 rounded-xl p-4 border ${result.hidden_count > 0 ? 'border-[#FFB800]/30 bg-[#FFB800]/5' : 'border-[#00C972]/30 bg-[#00C972]/5'}`}
                  data-testid="discoverability-panel"
                >
                  <div className="flex items-center gap-2 mb-1">
                    {result.hidden_count > 0
                      ? <AlertCircle className="text-[#FFB800]" size={18} />
                      : <CheckCircle2 className="text-[#00C972]" size={18} />}
                    <span className="text-[#0F0F0F] dark:text-white font-semibold">Brand Discover check</span>
                  </div>
                  <div className="text-sm text-[#4B5563] dark:text-zinc-300">
                    <span className="text-[#0F0F0F] dark:text-white font-bold">{result.discoverable_count}</span> of
                    {' '}<span className="text-[#0F0F0F] dark:text-white font-bold">{result.inserted_count + result.updated_count}</span> imported creators are now searchable by brands on
                    {' '}<a href="/brand/discover" className="text-[#FF451A] underline">Brand Discover</a>.
                    {result.hidden_count > 0 && (
                      <span className="text-[#FFB800]"> · {result.hidden_count} hidden (check debug endpoint)</span>
                    )}
                  </div>
                </div>
              )}

              {(result.recognized_columns || []).length > 0 && (
                <div className="mb-5">
                  <div className="text-xs uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-2">Recognized Columns</div>
                  <div className="flex flex-wrap gap-1.5">
                    {result.recognized_columns.map((c) => (
                      <span key={c} className="px-2 py-0.5 text-[11px] font-mono rounded-full bg-black/[0.04] dark:bg-white/5 text-[#374151] dark:text-zinc-300 border border-black/[0.10] dark:border-white/10">{c}</span>
                    ))}
                  </div>
                </div>
              )}

              {(result.failed_rows || []).length > 0 && (
                <div className="border border-[#FF3B30]/20 bg-[#FF3B30]/5 rounded-xl p-4">
                  <button
                    onClick={() => setErrOpen(!errOpen)}
                    className="w-full flex items-center justify-between text-left"
                    data-testid="error-rows-toggle"
                  >
                    <div className="flex items-center gap-2">
                      <AlertCircle className="text-[#FF3B30]" size={18} />
                      <span className="text-[#0F0F0F] dark:text-white font-semibold">
                        {result.skipped_count.toLocaleString()} {result.skipped_count === 1 ? 'row' : 'rows'} skipped
                        {result.skipped_count > result.failed_rows.length && <span className="font-normal text-[#6B6B6B]"> · showing first {result.failed_rows.length}</span>}
                      </span>
                    </div>
                    <span className="flex items-center gap-3">
                      {result.job_id && result.status === 'done' && (
                        <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); downloadSkipped(result.job_id); }} className="text-xs font-semibold text-[#FF451A] hover:underline" data-testid="download-skipped-rows">Download all skipped rows (CSV)</span>
                      )}
                      {errOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                    </span>
                  </button>
                  {errOpen && (
                    <div className="mt-3 overflow-x-auto" data-testid="error-rows-table">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[#6B6B6B] dark:text-zinc-500 font-mono uppercase text-[10px]">
                            <th className="text-left p-2">Row</th>
                            <th className="text-left p-2">Reason</th>
                            <th className="text-left p-2">Data</th>
                          </tr>
                        </thead>
                        <tbody>
                          {result.failed_rows.map((r, i) => (
                            <tr key={i} className="border-t border-black/[0.08] dark:border-white/5">
                              <td className="p-2 text-[#6B6B6B] dark:text-zinc-400 font-mono">{r.row}</td>
                              <td className="p-2 text-[#FF3B30]">{r.reason}</td>
                              <td className="p-2 text-[#6B6B6B] dark:text-zinc-500 max-w-xs truncate">
                                {Object.entries(r.data || {}).slice(0, 4).map(([k, v]) => `${k}=${v}`).join(' · ') || '—'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const downloadSkipped = async (jobId) => {
  try {
    const { data } = await axios.get(`${API_URL}/api/admin/import-jobs/${jobId}/skipped.csv`, { withCredentials: true, responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = `skipped-rows-${jobId.slice(-6)}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (e) {
    toast.error('Could not download skipped rows');
  }
};

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  if (e?.response?.status === 413) return 'File is too large to upload';
  if (!e?.response) return 'Network error — the upload did not reach the server';
  return `${fallback} (HTTP ${e.response.status})`;
};

const ImportProgress = ({ job }) => {
  const pct = job.total_rows ? Math.round((job.processed / job.total_rows) * 100) : 0;
  return (
    <div className="mt-5" data-testid="import-progress">
      <div className="flex justify-between text-xs text-[#6B6B6B] dark:text-zinc-400 mb-1.5">
        <span>Saving creators in batches of 500… {job.processed.toLocaleString()} / {job.total_rows.toLocaleString()} rows</span>
        <span className="font-mono" data-testid="import-progress-pct">{pct}%</span>
      </div>
      <div className="h-2 rounded-full bg-black/[0.06] dark:bg-white/10 overflow-hidden">
        <div className="h-full bg-[#FF451A] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>
      <div className="text-[11px] text-[#6B6B6B] mt-1.5">New {job.inserted_count.toLocaleString()} · updated {job.updated_count.toLocaleString()} · skipped {job.skipped_count.toLocaleString()}</div>
    </div>
  );
};

const Stat = ({ label, value, accent = '#FF451A', 'data-testid': testId }) => (
  <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-xl p-4" data-testid={testId}>
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1">{label}</div>
    <div className="text-2xl font-bold" style={{ color: accent }}>{value}</div>
  </div>
);

const Cell = ({ label, value, accent }) => (
  <div className="bg-black/[0.02] dark:bg-white/[0.03] border border-black/[0.08] dark:border-white/5 rounded-lg p-3">
    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] dark:text-zinc-500 mb-1">{label}</div>
    <div className={`text-2xl font-bold ${accent ? '' : 'text-[#0F0F0F] dark:text-white'}`} style={accent ? { color: accent } : undefined}>{value?.toLocaleString?.() ?? value}</div>
  </div>
);
